/**
 * Unified API Client
 * Базовый HTTP клиент с rate limiting и CSRF защитой
 */

import { useRateLimit } from '../utils/RateLimitHandler';
import { logger } from '../utils/logger';

// ===== Types =====

export interface ApiClientConfig {
  baseUrl?: string;
  csrfToken?: string;
  credentials?: RequestCredentials;
  defaultHeaders?: Record<string, string>;
}

export interface ApiResponse<T = unknown> {
  data?: T;
  success?: boolean;
  message?: string;
  [key: string]: unknown;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export interface RequestConfig extends Omit<RequestInit, 'method' | 'body'> {
  method?: HttpMethod;
  body?: BodyInit | Record<string, unknown>;
  rateLimitKey?: string;
  skipRateLimit?: boolean;
}

// ===== Singleton State =====

let globalConfig: ApiClientConfig = {};

// Helper to normalize headers to Record<string, string>
const normalizeHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => { result[key] = value; });
    return result;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return headers as Record<string, string>;
};

const getBaseUrl = (): string => {
  if (globalConfig.baseUrl) return globalConfig.baseUrl;

  const base = document.querySelector('base');
  if (base?.href) {
    const url = base.href.endsWith('/') ? base.href : `${base.href}/`;
    globalConfig.baseUrl = url;
    return url;
  }

  const fallback = `${window.location.origin}/`;
  logger.log('ApiClient: using fallback URL:', fallback);
  return fallback;
};

const getCsrfToken = (): string => {
  if (globalConfig.csrfToken) return globalConfig.csrfToken;

  const meta = document.querySelector('meta[name="csrf-token"]');
  const token = meta?.getAttribute('content') || '';
  globalConfig.csrfToken = token;
  return token;
};

const buildUrl = (path: string, params?: Record<string, string | number | undefined>): string => {
  const base = getBaseUrl().replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  if (!params || Object.keys(params).length === 0) {
    return `${base}${cleanPath}`;
  }

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  return `${base}${cleanPath}?${searchParams.toString()}`;
};

const buildFormBody = (data: Record<string, unknown>): string => {
  return Object.entries(data)
    .map(([key, value]) => {
      const strValue = typeof value === 'string' ? value : JSON.stringify(value);
      return `${encodeURIComponent(key)}=${encodeURIComponent(strValue)}`;
    })
    .join('&');
};

// ===== ApiClient Factory =====

export function createApiClient(config: ApiClientConfig = {}) {
  // Merge with global config
  globalConfig = { ...globalConfig, ...config };

  const { isRateLimited, getTimeToRetry, checkAndHandleRateLimit } = useRateLimit();

  const request = async <T = unknown>(
    method: HttpMethod,
    url: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> => {
    const { rateLimitKey, skipRateLimit = false, body, ...fetchConfig } = config;

    // Offline guard: fail fast instead of waiting for network timeout
    if (!navigator.onLine) {
      throw new Error('Network offline');
    }

    // Rate limiting check
    if (rateLimitKey && !skipRateLimit && isRateLimited(rateLimitKey)) {
      throw new Error(`Rate limited. Retry in ${getTimeToRetry(rateLimitKey)}ms`);
    }

    // Prepare headers
    const headers: Record<string, string> = {
      'X-Requested-With': 'XMLHttpRequest',
      ...globalConfig.defaultHeaders,
      ...normalizeHeaders(fetchConfig.headers),
    };

    // Add CSRF token to headers
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    // Prepare body
    let finalBody: BodyInit | undefined;
    if (body) {
      if (body instanceof FormData || body instanceof URLSearchParams || typeof body === 'string') {
        finalBody = body;
      } else if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
        finalBody = body as BodyInit;
      } else {
        // Object body - use form-urlencoded for compatibility
        finalBody = buildFormBody(body as Record<string, unknown>);
        headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
      }
    }

    // Make request
    const response = await fetch(url, {
      method,
      credentials: config.credentials || globalConfig.credentials || 'include',
      headers,
      body: finalBody,
      ...fetchConfig,
    });

    // Check rate limit
    if (rateLimitKey && !skipRateLimit) {
      await checkAndHandleRateLimit(response, rateLimitKey);
    }

    // Handle errors
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Parse JSON response
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      const text = await response.text();
      console.error('Server returned non-JSON response:', text);
      throw new Error('Invalid response format');
    }

    return response.json();
  };

  // Convenience methods
  const get = <T = unknown>(url: string, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>('GET', url, config);

  const post = <T = unknown>(url: string, body?: RequestConfig['body'], config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>('POST', url, { ...config, body });

  const put = <T = unknown>(url: string, body?: RequestConfig['body'], config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>('PUT', url, { ...config, body });

  const del = <T = unknown>(url: string, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>('DELETE', url, config);

  return {
    request,
    get,
    post,
    put,
    delete: del,
    buildUrl,
    getBaseUrl,
    getCsrfToken,
  };
}

// Global API client instance
let globalApiClient: ReturnType<typeof createApiClient> | null = null;

export function useApiClient(config?: ApiClientConfig) {
  if (!globalApiClient || config) {
    globalApiClient = createApiClient(config);
  }
  return globalApiClient;
}

// Reset for testing - intentionally exported but not used in production
export function resetApiClient(): void {
  globalApiClient = null;
  globalConfig = {};
}
